import { Box } from '@chakra-ui/react';
import { WrappedImage } from '@time-line/components/common';
import type { SanityImage } from '@time-line/components/types';
import { ProductBadgeContained } from 'components/product/ProductBadge/ProductBadgeContained';
import { useSanityProduct } from 'hooks/product/useSanityProduct';
import Image from 'next/image';
import Link from 'next/link';
import type { ProductComparison } from 'types/cms/blocks';

import { calcImageWidth } from './utils/calcImageWidth';

interface ProductImageProps {
  image?: SanityImage;
  formatThumbnail?: SanityImage;
  shopifyImageUrl?: string;
  href: string;
  slug: string;
  imageType: ProductComparison['imageType'];
  productsCount: number;
}

export function ProductImage({
  image,
  formatThumbnail,
  shopifyImageUrl,
  href,
  slug,
  imageType,
  productsCount = 3,
}: ProductImageProps) {
  const { images, title, productBullet } = useSanityProduct(slug, {
    required: true,
  });

  if (imageType === 'format') {
    const formatImage = image || formatThumbnail || images?.[0];
    const formatSizes = '(min-width: 768px) 100px, 80px';

    if (!formatImage && !shopifyImageUrl) return null;

    return (
      <Box
        as={Link}
        display="block"
        href={href}
        pos="relative"
        boxSize={{ base: '80px', md: '100px' }}
        flexShrink={0}
        borderRadius="base"
        overflow="hidden"
      >
        {formatImage ? (
          <WrappedImage ratio={1} image={formatImage} sizes={formatSizes} />
        ) : (
          shopifyImageUrl && (
            <Image
              src={shopifyImageUrl}
              alt={title}
              fill
              sizes={formatSizes}
              style={{ objectFit: 'cover' }}
            />
          )
        )}
      </Box>
    );
  }

  const widths = calcImageWidth(productsCount);
  const sizes = `(min-width: 1540px) ${widths.max}, (min-width: 960px) ${widths.lg}, (min-width: 768px) ${widths.md}, 42vw`;

  if (!image && shopifyImageUrl) {
    return (
      <Box
        as={Link}
        display="block"
        href={href}
        pos="relative"
        w="full"
        aspectRatio={1}
        borderRadius="base"
        overflow="hidden"
        sx={{ containerType: 'inline-size' }}
      >
        <Image
          src={shopifyImageUrl}
          alt={title}
          fill
          sizes={sizes}
          style={{ objectFit: 'cover' }}
        />
        <Box pos="absolute" top={0} left={0} zIndex={1}>
          <ProductBadgeContained productBullet={productBullet} />
        </Box>
      </Box>
    );
  }

  const fallbackImage = image || images[0];

  if (!fallbackImage) return null;

  return (
    <Box
      as={Link}
      display="block"
      href={href}
      pos="relative"
      w="full"
      borderRadius="base"
      overflow="hidden"
      sx={{ containerType: 'inline-size' }}
    >
      <Box pos="absolute" top={0} left={0}>
        <ProductBadgeContained productBullet={productBullet} />
      </Box>
      <WrappedImage ratio={1} image={fallbackImage} sizes={sizes} />
    </Box>
  );
}
